import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { usePostHog } from "posthog-js/react";

interface UpgradeSurveyDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: (reasons: string[]) => void;
}

const SURVEY_REASONS = [
  { id: "price", label: "Too expensive for me right now" },
  {
    id: "paymentMethodMissing",
    label: "I couldn't use my preferred payment method",
  },
  { id: "cardNotWorking", label: "My cards were not working" },
  {
    id: "value_unclear",
    label: "I wasn't sure the premium features were worth it",
  },
  { id: "technical_issues", label: "I had technical or checkout problems" },
  { id: "later", label: "I want to decide later" },
  { id: "missing_currency", label: "My currency is missing" },
  { id: "other", label: "Other (please tell us)" },
];

export function UpgradeSurveyDialog({
  isOpen,
  onOpenChange,
  onSubmit,
}: UpgradeSurveyDialogProps) {
  const posthog = usePostHog();
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [otherReasonText, setOtherReasonText] = useState("");
  const [preferredPayment, setPreferredPayment] = useState("");
  const [technicalIssue, setTechnicalIssue] = useState("");
  const [missingCurrencyText, setMissingCurrencyText] = useState("");

  const handleCheckboxChange = (reasonId: string, checked: boolean) => {
    setSelectedReasons((prev) =>
      checked ? [...prev, reasonId] : prev.filter((r) => r !== reasonId)
    );
  };

  const handleSubmit = () => {
    const reasonsToSubmit = selectedReasons
      .map((reasonId) => {
        switch (reasonId) {
          case "other":
            return `Other: ${otherReasonText}`;
          case "paymentMethodMissing":
            return `Wanted payment method: ${preferredPayment}`;
          case "technical_issues":
            return `Technical issue: ${technicalIssue}`;
          case "missing_currency":
            return `Missing currency: ${missingCurrencyText}`;
          default: {
            const reasonObj = SURVEY_REASONS.find((r) => r.id === reasonId);
            return reasonObj ? reasonObj.label : "";
          }
        }
      })
      .filter(Boolean);

    onSubmit(reasonsToSubmit);
    onOpenChange(false);

    // reset local state
    setSelectedReasons([]);
    setOtherReasonText("");
    setPreferredPayment("");
    setTechnicalIssue("");
    setMissingCurrencyText("");
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="sm:max-w-[480px] overflow-y-auto"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>Why didn&rsquo;t you upgrade?</DialogTitle>
          <DialogDescription>
            Your feedback helps us improve Flashka for students like you.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {SURVEY_REASONS.map((reason) => (
            <div key={reason.id} className="flex items-start space-x-2">
              <Checkbox
                id={reason.id}
                checked={selectedReasons.includes(reason.id)}
                onCheckedChange={(checked) =>
                  handleCheckboxChange(reason.id, !!checked)
                }
              />
              <Label htmlFor={reason.id} className="font-normal leading-snug">
                {reason.label}
              </Label>
            </div>
          ))}

          {/* Secret discount for "price" */}
          {selectedReasons.includes("price") && (
            <div className="w-full rounded-md bg-green-50 p-3 text-sm text-center">
              <p>
                🤫 Pssst… here&rsquo;s a <strong>30 % student discount</strong>{" "}
                just for you!
              </p>
              <p className="">
                <b>CHECK30</b>
              </p>
            </div>
          )}

          {/* Preferred payment method field */}
          {selectedReasons.includes("paymentMethodMissing") && (
            <div className="grid w-full gap-1.5">
              <Label htmlFor="preferred_payment">
                Preferred payment method:
              </Label>
              <input
                id="preferred_payment"
                type="text"
                placeholder="e.g. PayPal, PIX, M-Pesa…"
                value={preferredPayment}
                onChange={(e) => setPreferredPayment(e.target.value)}
                className="w-full rounded-md border p-2"
              />
            </div>
          )}

          {/* Technical issue field */}
          {selectedReasons.includes("technical_issues") && (
            <div className="grid w-full gap-1.5">
              <Label htmlFor="technical_issue">What went wrong?</Label>
              <textarea
                id="technical_issue"
                placeholder="Describe the issue you faced…"
                value={technicalIssue}
                onChange={(e) => setTechnicalIssue(e.target.value)}
                className="w-full min-h-[80px] rounded-md border p-2"
              />
            </div>
          )}

          {/* Missing currency field */}
          {selectedReasons.includes("missing_currency") && (
            <div className="grid w-full gap-1.5">
              <Label htmlFor="missing_currency_text">
                Which currency were you looking for?
              </Label>
              <input
                id="missing_currency_text"
                type="text"
                placeholder="e.g. CAD, AUD, INR..."
                value={missingCurrencyText}
                onChange={(e) => setMissingCurrencyText(e.target.value)}
                className="w-full rounded-md border p-2"
              />
            </div>
          )}

          {/* Generic "other" textarea */}
          {selectedReasons.includes("other") && (
            <div className="grid w-full gap-1.5">
              <Label htmlFor="other_reason_text">
                Please specify your reason:
              </Label>
              <textarea
                id="other_reason_text"
                placeholder="Type your reason here…"
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
                className="w-full min-h-[80px] rounded-md border p-2"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
              posthog.capture("Cancelled-Checkout-Cancel-Survey");
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="primary">
            Submit Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
